const PROGRESS_THRESHOLD = 75;

export type EpisodeProgressInfo = {
  progressPercentage: number;
  lastListenedUtc?: string;
  isCompleted?: boolean;
};

export type SuggestionInput<T> = {
  episodes: Array<T>;
  getEpisodeId: (episode: T) => string;
  getReleaseDate: (episode: T) => string | null | undefined;
  getProgress: (episodeId: string) => EpisodeProgressInfo | undefined;
};

/**
 * Finds the episode with the most recent lastListenedUtc timestamp.
 * Returns the index and progress info, or null if no episode has been listened to.
 */
function findLastListenedEpisode<T>(
  sortedEpisodes: Array<T>,
  getEpisodeId: (episode: T) => string,
  getProgress: (episodeId: string) => EpisodeProgressInfo | undefined,
): { index: number; progress: EpisodeProgressInfo } | null {
  let result: { index: number; progress: EpisodeProgressInfo; timestamp: number } | null = null;

  for (let i = 0; i < sortedEpisodes.length; i++) {
    const episodeId = getEpisodeId(sortedEpisodes[i]);
    const progress = getProgress(episodeId);

    if (progress?.lastListenedUtc) {
      const timestamp = new Date(progress.lastListenedUtc).getTime();
      if (!Number.isNaN(timestamp) && (!result || timestamp > result.timestamp)) {
        result = { index: i, progress, timestamp };
      }
    }
  }

  return result ? { index: result.index, progress: result.progress } : null;
}

/**
 * Checks if an episode is considered completed based on progress data.
 */
function isEpisodeCompleted(progress: EpisodeProgressInfo | undefined): boolean {
  if (!progress) return false;
  if (progress.isCompleted) return true;
  return progress.progressPercentage >= PROGRESS_THRESHOLD;
}

/**
 * Core suggestion logic. Sorts episodes and returns the suggested index in the sorted array.
 */
function computeSuggestion<T>(
  episodes: Array<T>,
  getEpisodeId: (episode: T) => string,
  getReleaseDate: (episode: T) => string | null | undefined,
  getProgress: (episodeId: string) => EpisodeProgressInfo | undefined,
): { sortedEpisodes: Array<T>; suggestedIndex: number } {
  if (episodes.length === 0) {
    return { sortedEpisodes: [], suggestedIndex: 0 };
  }

  // Sort episodes by releaseDate (ascending)
  const sortedEpisodes = [...episodes].sort((a, b) => {
    const dateA = getReleaseDate(a);
    const dateB = getReleaseDate(b);
    const timeA = dateA ? new Date(dateA).getTime() : 0;
    const timeB = dateB ? new Date(dateB).getTime() : 0;
    return timeA - timeB;
  });

  // Find the last listened episode
  const lastListened = findLastListenedEpisode(sortedEpisodes, getEpisodeId, getProgress);

  // If no episode has been listened to, suggest the first episode
  if (!lastListened) {
    return { sortedEpisodes, suggestedIndex: 0 };
  }

  const { index: lastListenedIndex, progress: lastListenedProgress } = lastListened;

  // If progress is between 0% and 75% (exclusive), suggest this episode
  if (lastListenedProgress.progressPercentage > 0 && lastListenedProgress.progressPercentage < PROGRESS_THRESHOLD) {
    return { sortedEpisodes, suggestedIndex: lastListenedIndex };
  }

  // If progress is >= 75%, suggest the next incomplete episode
  if (lastListenedProgress.progressPercentage >= PROGRESS_THRESHOLD) {
    for (let i = lastListenedIndex + 1; i < sortedEpisodes.length; i++) {
      const candidateId = getEpisodeId(sortedEpisodes[i]);
      if (!isEpisodeCompleted(getProgress(candidateId))) {
        return { sortedEpisodes, suggestedIndex: i };
      }
    }
    // All episodes after are completed, suggest the last listened episode
    return { sortedEpisodes, suggestedIndex: lastListenedIndex };
  }

  // Fallback: suggest the first episode (covers edge case of 0% progress)
  return { sortedEpisodes, suggestedIndex: 0 };
}

/**
 * Returns the suggested episode index in the **original** (unsorted) array.
 */
export function getSuggestedEpisodeIndex<T>(input: SuggestionInput<T>): number {
  const { episodes, getEpisodeId, getReleaseDate, getProgress } = input;

  if (episodes.length === 0) return 0;

  // Build mapping from episode ID to original index
  const idToOriginalIndex = new Map<string, number>();
  for (let i = 0; i < episodes.length; i++) {
    idToOriginalIndex.set(getEpisodeId(episodes[i]), i);
  }

  const { sortedEpisodes, suggestedIndex } = computeSuggestion(episodes, getEpisodeId, getReleaseDate, getProgress);
  const suggestedId = getEpisodeId(sortedEpisodes[suggestedIndex]);

  return idToOriginalIndex.get(suggestedId) ?? 0;
}

/**
 * Returns the suggested episode index in the **sorted** array, along with the sorted array.
 */
export function getSuggestedEpisodeIndexSorted<T>(input: SuggestionInput<T>): {
  sortedEpisodes: Array<T>;
  suggestedIndex: number;
} {
  const { episodes, getEpisodeId, getReleaseDate, getProgress } = input;
  return computeSuggestion(episodes, getEpisodeId, getReleaseDate, getProgress);
}
